import { supabase } from './client';
import {
  SUPABASE_STORAGE_ROOT,
  STORAGE_FOLDERS,
  FOLDER_TO_TYPE,
  EXTENSION_TO_TYPE,
  MediaFileType,
} from '../utils/constants';

export interface MediaFile {
  name: string;        // Full filename with extension (e.g., "POE_xxx.webm")
  path: string;        // Full path in storage (e.g., "videos/POE_xxx.webm")
  url: string;         // Full public URL
  type: MediaFileType; // 'video' | 'audio' | 'model' | 'scene' | 'image' | 'texture'
  extension: string;   // File extension (e.g., ".webm")
}

export interface MediaBundle {
  baseName: string;
  files: MediaFile[];
  video?: MediaFile;   // Convenience: first video file found
  audio?: MediaFile;   // Convenience: first audio file found
  model?: MediaFile;   // Convenience: first model file found
  scene?: MediaFile;   // Convenience: first scene file found
}

/**
 * Extract the base name from a filename (without extension)
 */
function getBaseName(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.substring(0, lastDot) : filename;
}

/**
 * Extract the extension from a filename (including the dot)
 */
function getExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.substring(lastDot).toLowerCase() : '';
}

/**
 * Determine the media type from folder name and file extension
 */
function getMediaType(folder: string, extension: string): MediaFileType {
  // Prefer extension-based type if available
  if (EXTENSION_TO_TYPE[extension]) {
    return EXTENSION_TO_TYPE[extension];
  }
  // Fall back to folder-based type
  return FOLDER_TO_TYPE[folder] || 'image';
}

/**
 * Search all storage folders for files matching the given base name.
 * Returns a MediaBundle containing all found files categorized by type.
 *
 * @param baseName - The base name to search for (e.g., "POE_who_are_you_whats_your_story")
 * @returns MediaBundle with all matching files
 */
export async function searchMediaBundle(baseName: string): Promise<MediaBundle> {
  const bundle: MediaBundle = {
    baseName,
    files: [],
  };

  // Search each folder in parallel
  const searchPromises = STORAGE_FOLDERS.map(async (folder) => {
    try {
      const { data, error } = await supabase.storage
        .from('babylon')
        .list(folder, {
          limit: 100,
          search: baseName,
        });

      if (error) {
        console.warn(`[StorageSearch] Error listing ${folder}:`, error.message);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Filter files that match the exact base name
      const matchingFiles: MediaFile[] = [];
      for (const file of data) {
        const fileBaseName = getBaseName(file.name);
        if (fileBaseName === baseName) {
          const extension = getExtension(file.name);
          const path = `${folder}/${file.name}`;
          matchingFiles.push({
            name: file.name,
            path,
            url: `${SUPABASE_STORAGE_ROOT}${path}`,
            type: getMediaType(folder, extension),
            extension,
          });
        }
      }

      return matchingFiles;
    } catch (err) {
      console.warn(`[StorageSearch] Failed to search ${folder}:`, err);
      return [];
    }
  });

  // Wait for all searches to complete
  const results = await Promise.all(searchPromises);

  // Flatten results into bundle.files
  for (const folderFiles of results) {
    bundle.files.push(...folderFiles);
  }

  // Set convenience properties for first file of each type
  for (const file of bundle.files) {
    if (file.type === 'video' && !bundle.video) {
      bundle.video = file;
    } else if (file.type === 'audio' && !bundle.audio) {
      bundle.audio = file;
    } else if (file.type === 'model' && !bundle.model) {
      bundle.model = file;
    } else if (file.type === 'scene' && !bundle.scene) {
      bundle.scene = file;
    }
  }

  console.log(`[StorageSearch] Found ${bundle.files.length} files for "${baseName}":`,
    bundle.files.map(f => f.path));

  return bundle;
}
